import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, doc, updateDoc, onSnapshot, or, orderBy } from 'firebase/firestore';

const useRequests = (userId) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRequestsCount, setNewRequestsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  useEffect(() => {
    const subscribeToRequests = (userId, callback) => {
      const requestsQuery = query(
        collection(db, 'requests'),
        or(
          where('ownerId', '==', userId),
          where('borrowerId', '==', userId)
        ),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(requestsQuery, (snapshot) => {
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: doc.data().ownerId === userId ? 'inbound' : 'outbound'
        }));
        callback(requestsData);
      });
    };

    let unsubscribe = () => {};

    if (userId) {
      setLoading(true);
      unsubscribe = subscribeToRequests(userId, (requestsData) => {
        setRequests(requestsData);
        setLoading(false);

        // Count new requests and unread messages
        const newRequests = requestsData.filter(req => req.status === 'pending' && req.type === 'inbound' && !req.viewed);
        const unreadMessages = requestsData.filter(req => req.unreadMessages > 0);
        
        setNewRequestsCount(newRequests.length);
        setUnreadMessagesCount(unreadMessages.reduce((total, req) => total + req.unreadMessages, 0));
      });
    }

    return () => unsubscribe();
  }, [userId]);

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, { status: newStatus, viewed: true });
      // The local state will be automatically updated by the onSnapshot listener
    } catch (error) {
      console.error('Error updating request status:', error);
    }
  };

  const markRequestAsViewed = async (requestId) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, { viewed: true });
    } catch (error) {
      console.error('Error marking request as viewed:', error);
    }
  };

  const markMessagesAsRead = async (requestId) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, { unreadMessages: 0 });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Separate inbound and outbound requests
  const inboundRequests = requests.filter(req => req.type === 'inbound');
  const outboundRequests = requests.filter(req => req.type === 'outbound');

  return { 
    inboundRequests, 
    outboundRequests, 
    loading, 
    updateRequestStatus, 
    markRequestAsViewed, 
    markMessagesAsRead,
    newRequestsCount,
    unreadMessagesCount
  };
};

export default useRequests;
